import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Check, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeeField = ({ label, description, value, onChange, suffix = '%' }) => (
  <div className="space-y-2">
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <p className="text-xs text-gray-600">{description}</p>
    </div>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="flex-1"
      />
      <span className="text-sm font-medium w-8 text-right">{suffix}</span>
    </div>
  </div>
);

export const SettingsTab = ({ token }) => {
  const [fees, setFees] = useState({});
  const [limits, setLimits] = useState({});
  const [security, setSecurity] = useState({
    security_otp_amount_threshold: 375,
    security_otp_velocity_count: 3,
    security_otp_velocity_window: 3600,
    security_biometric_enabled: false,
    security_biometric_fallback_otp: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSecurityChanges, setHasSecurityChanges] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [feesRes, securityRes] = await Promise.all([
        axios.get(`${API}/admin/settings/fees`, { headers }),
        axios.get(`${API}/admin/settings/security`, { headers }),
      ]);

      const feeData = {};
      const limitData = {};
      Object.entries(feesRes.data.fees || {}).forEach(([key, val]) => {
        feeData[key] = parseFloat(val.value) || 0;
      });
      Object.entries(feesRes.data.limits || {}).forEach(([key, val]) => {
        limitData[key] = parseFloat(val.value) || 0;
      });
      setFees(feeData);
      setLimits(limitData);

      const secData = {};
      Object.entries(securityRes.data.security || {}).forEach(([key, val]) => {
        const raw = val.value;
        if (val.type === 'bool') secData[key] = raw === 'true' || raw === '1' || raw === 'yes';
        else if (val.type === 'int') secData[key] = parseInt(raw) || 0;
        else secData[key] = parseFloat(raw) || 0;
      });
      if (Object.keys(secData).length) setSecurity(prev => ({ ...prev, ...secData }));

      setHasChanges(false);
      setHasSecurityChanges(false);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFeeChange = (key, value) => {
    setFees({ ...fees, [key]: value });
    setHasChanges(true);
  };

  const handleLimitChange = (key, value) => {
    setLimits({ ...limits, [key]: value });
    setHasChanges(true);
  };

  const handleSecurityChange = (key, value) => {
    setSecurity({ ...security, [key]: value });
    setHasSecurityChanges(true);
  };

  const handleSaveSettings = async (settingsType) => {
    setSaving(true);
    try {
      const settingsToSave = settingsType === 'fees' ? fees : limits;
      await axios.put(`${API}/admin/settings/fees`, { settings: settingsToSave }, { headers });
      toast.success(`${settingsType === 'fees' ? 'Fee' : 'Limit'} settings updated successfully`);
      setHasChanges(false);
      fetchSettings();
    } catch (error) {
      toast.error(`Failed to update ${settingsType === 'fees' ? 'fee' : 'limit'} settings`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecuritySettings = async () => {
    setSavingSecurity(true);
    try {
      const payload = {
        security_otp_amount_threshold: String(security.security_otp_amount_threshold),
        security_otp_velocity_count: String(security.security_otp_velocity_count),
        security_otp_velocity_window: String(security.security_otp_velocity_window),
        security_biometric_enabled: String(security.security_biometric_enabled),
        security_biometric_fallback_otp: String(security.security_biometric_fallback_otp),
      };
      await axios.put(`${API}/admin/settings/security`, { settings: payload }, { headers });
      toast.success('Security settings updated successfully');
      setHasSecurityChanges(false);
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update security settings');
    } finally {
      setSavingSecurity(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fee Structure */}
      <Card>
        <CardHeader>
          <CardTitle>Fee Structure</CardTitle>
          <CardDescription>Transaction fees and commissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FeeField
              label="Debit Card Fee"
              description="Fee charged for debit card transactions"
              value={fees.fee_debit_card || 0}
              onChange={(val) => handleFeeChange('fee_debit_card', val)}
            />
            <FeeField
              label="Credit Card Fee"
              description="Fee charged for credit card transactions"
              value={fees.fee_credit_card || 0}
              onChange={(val) => handleFeeChange('fee_credit_card', val)}
            />
            <FeeField
              label="Card Payout Fee"
              description="Fee charged for card payout transactions"
              value={fees.fee_card_payout || 0}
              onChange={(val) => handleFeeChange('fee_card_payout', val)}
            />
            <FeeField
              label="Wallet Transfer Fee"
              description="Fee charged for wallet-to-wallet transfers"
              value={fees.fee_wallet_transfer || 0}
              onChange={(val) => handleFeeChange('fee_wallet_transfer', val)}
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Money Transfer Fees</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeeField
                label="Money Transfer Rate"
                description="Base fee per transaction"
                value={fees.fee_money_transfer_rate || 0}
                onChange={(val) => handleFeeChange('fee_money_transfer_rate', val)}
                suffix="ZAR"
              />
              <FeeField
                label="Money Transfer Base Amount"
                description="Minimum transaction amount"
                value={fees.fee_money_transfer_base || 0}
                onChange={(val) => handleFeeChange('fee_money_transfer_base', val)}
                suffix="ZAR"
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Commission Rates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeeField
                label="Airtime & Data Commission"
                description="Commission charged on airtime/data sales"
                value={fees.commission_airtime_data || 0}
                onChange={(val) => handleFeeChange('commission_airtime_data', val)}
              />
              <FeeField
                label="Money Voucher Commission"
                description="Commission charged on voucher sales"
                value={fees.commission_money_voucher || 0}
                onChange={(val) => handleFeeChange('commission_money_voucher', val)}
                suffix="ZAR"
              />
              <FeeField
                label="Insurance Commission"
                description="Commission charged on insurance premiums"
                value={fees.commission_insurance || 0}
                onChange={(val) => handleFeeChange('commission_insurance', val)}
                suffix="ZAR"
              />
              <FeeField
                label="ID Verification Commission"
                description="Commission charged on ID verification"
                value={fees.commission_id_verify || 0}
                onChange={(val) => handleFeeChange('commission_id_verify', val)}
                suffix="ZAR"
              />
            </div>
          </div>

          <div className="pt-4">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]" disabled={!hasChanges || saving}>
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2" size={16} />
                      Save Fee Settings
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Fee Changes</AlertDialogTitle>
                  <AlertDialogDescription>
                    These changes will take effect immediately. All new transactions will use the updated rates.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogAction onClick={() => handleSaveSettings('fees')}>
                  Confirm Changes
                </AlertDialogAction>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Limits</CardTitle>
          <CardDescription>Daily limits and maximum transaction amounts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FeeField
              label="Customer Daily Limit"
              description="Daily transaction limit for customers"
              value={limits.limit_customer_daily || 0}
              onChange={(val) => handleLimitChange('limit_customer_daily', val)}
              suffix="ZAR"
            />
            <FeeField
              label="Merchant Daily Limit"
              description="Daily transaction limit for merchants"
              value={limits.limit_merchant_daily || 0}
              onChange={(val) => handleLimitChange('limit_merchant_daily', val)}
              suffix="ZAR"
            />
            <FeeField
              label="Verified Customer Daily Limit"
              description="Daily limit for verified customers"
              value={limits.limit_verified_customer_daily || 0}
              onChange={(val) => handleLimitChange('limit_verified_customer_daily', val)}
              suffix="ZAR"
            />
            <FeeField
              label="Verified Merchant Daily Limit"
              description="Daily limit for verified merchants"
              value={limits.limit_verified_merchant_daily || 0}
              onChange={(val) => handleLimitChange('limit_verified_merchant_daily', val)}
              suffix="ZAR"
            />
            <FeeField
              label="Max Wallet Transfer"
              description="Maximum single wallet transfer amount"
              value={limits.limit_max_wallet_transfer || 0}
              onChange={(val) => handleLimitChange('limit_max_wallet_transfer', val)}
              suffix="ZAR"
            />
            <FeeField
              label="Max Voucher Amount"
              description="Maximum money voucher amount"
              value={limits.limit_max_voucher || 0}
              onChange={(val) => handleLimitChange('limit_max_voucher', val)}
              suffix="ZAR"
            />
            <FeeField
              label="Max Card Payment"
              description="Maximum card payment amount"
              value={limits.limit_max_card_payment || 0}
              onChange={(val) => handleLimitChange('limit_max_card_payment', val)}
              suffix="ZAR"
            />
          </div>

          <div className="pt-4">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]" disabled={!hasChanges || saving}>
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2" size={16} />
                      Save Limit Settings
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Limit Changes</AlertDialogTitle>
                  <AlertDialogDescription>
                    These changes will take effect immediately.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogAction onClick={() => handleSaveSettings('limits')}>
                  Confirm Changes
                </AlertDialogAction>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Security & Authentication Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield size={18} />
            Security &amp; Authentication
          </CardTitle>
          <CardDescription>
            Step-up OTP thresholds, velocity limits, and biometric authentication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* OTP Thresholds */}
          <div>
            <h3 className="font-semibold text-sm mb-3 text-gray-700 uppercase tracking-wide">OTP Triggers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeeField
                label="Amount Threshold (ZAR)"
                description="Transactions at or above this amount require OTP verification"
                value={security.security_otp_amount_threshold}
                onChange={(val) => handleSecurityChange('security_otp_amount_threshold', val)}
                suffix="ZAR"
              />
              <FeeField
                label="Velocity Limit (count)"
                description="Number of transactions per user within the time window before OTP is triggered regardless of amount"
                value={security.security_otp_velocity_count}
                onChange={(val) => handleSecurityChange('security_otp_velocity_count', Math.max(1, Math.round(val)))}
                suffix="txns"
              />
              <FeeField
                label="Velocity Window (seconds)"
                description="Time window in seconds for velocity limit (3600 = 1 hour)"
                value={security.security_otp_velocity_window}
                onChange={(val) => handleSecurityChange('security_otp_velocity_window', Math.max(60, Math.round(val)))}
                suffix="sec"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Current rule: OTP required when amount ≥ R{security.security_otp_amount_threshold}
              {' '}OR when user exceeds {security.security_otp_velocity_count} transactions
              {' '}within {Math.round(security.security_otp_velocity_window / 60)} minutes.
            </p>
          </div>

          <Separator />

          {/* Biometric */}
          <div>
            <h3 className="font-semibold text-sm mb-3 text-gray-700 uppercase tracking-wide">Biometric Authentication</h3>
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4 p-3 border rounded-lg bg-gray-50">
                <div>
                  <p className="text-sm font-medium">Enable Biometric Step-up</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, qualifying transactions will prompt for device biometric (fingerprint / Face ID)
                    instead of OTP. Requires WebAuthn-compatible browser and registered device credential.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleSecurityChange('security_biometric_enabled', !security.security_biometric_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    security.security_biometric_enabled ? 'bg-[#145539]' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    security.security_biometric_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {security.security_biometric_enabled && (
                <div className="flex items-start justify-between gap-4 p-3 border rounded-lg bg-blue-50">
                  <div>
                    <p className="text-sm font-medium">Fallback to OTP if Biometric Unavailable</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      If the user's device does not support biometric or authentication fails,
                      automatically fall back to OTP. Recommended: ON.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSecurityChange('security_biometric_fallback_otp', !security.security_biometric_fallback_otp)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      security.security_biometric_fallback_otp ? 'bg-[#145539]' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      security.security_biometric_fallback_otp ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              )}

              {!security.security_biometric_enabled && (
                <p className="text-xs text-gray-400 italic">
                  Biometric is disabled — all step-up challenges use OTP.
                </p>
              )}
            </div>
          </div>

          <div className="pt-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="w-full bg-[#1B0C34] hover:bg-[#2d1654] text-white"
                  disabled={!hasSecurityChanges || savingSecurity}
                >
                  {savingSecurity ? (
                    <><Loader2 className="animate-spin mr-2" size={16} />Saving...</>
                  ) : (
                    <><Shield className="mr-2" size={16} />Save Security Settings</>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Security Changes</AlertDialogTitle>
                  <AlertDialogDescription>
                    These changes take effect immediately for all new transactions.
                    Ensure you understand the impact before confirming.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogAction onClick={handleSaveSecuritySettings}>
                  Confirm Changes
                </AlertDialogAction>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
